import React, { useEffect, useState, useMemo } from 'react';
import { MdRefresh, MdDelete, MdCheckCircle, MdAccessTime, MdLocationOn, MdPerson, MdEmail, MdCalendarToday, MdDescription, MdEventAvailable, MdCancel, MdHourglassEmpty, MdEventBusy, MdLoop, MdInsertChart, MdStar, MdStarBorder, MdFeedback, MdSearch, MdDownload } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import './AllBookings.css';
import './BookRoom.css';

/**
 * AllBookings Component (ADMIN ONLY)
 *
 * Displays all approved and confirmed bookings across all users
 */
function AllBookings() {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBookings, setSelectedBookings] = useState([]); // For bulk delete
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackBooking, setFeedbackBooking] = useState(null);
  const [feedbackData, setFeedbackData] = useState({
    rating: 5,
    roomClean: true,
    equipmentOk: true,
    endedOnTime: true,
    comments: ''
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchAllBookings();
  }, []);

  const fetchAllBookings = async () => {
    try {
      setIsLoading(true);
      const response = await conferenceRoomApi.getAllBookingRequests();
      const bookingsData = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];

      // Sort bookings by startTime in descending order (latest first)
      const sortedBookings = bookingsData.sort((a, b) => {
        return new Date(b.startTime) - new Date(a.startTime);
      });

      // Show ALL bookings regardless of status
      console.log('All Bookings fetched:', sortedBookings.length);
      setBookings(sortedBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings', {
        position: "top-right",
        autoClose: 3000,
      });
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getBookingStatus = (booking) => {
    // Backend status takes priority
    if (booking.status === 'CANCELLED') return 'cancelled';
    if (booking.status === 'PENDING') return 'pending';
    if (booking.status === 'REJECTED') return 'rejected';

    const now = new Date();
    const startTime = new Date(booking.startTime);
    const endTime = new Date(booking.endTime);

    // For approved/confirmed bookings
    if (booking.status === 'APPROVED' || booking.status === 'CONFIRMED') {
      if (endTime < now) return 'completed';
      if (startTime <= now && endTime >= now) return 'ongoing';
      return 'confirmed';
    }

    return 'unknown';
  };

  const filteredBookings = useMemo(() => {
    // First, filter to show only parent bookings or non-recurring bookings
    let uniqueBookings = bookings.filter(booking => {
      // Show non-recurring bookings (no recurringGroupId)
      if (!booking.recurringGroupId) return true;
      // For recurring bookings, show only parent booking
      return booking.isParentBooking === true;
    });

    // Apply status filter
    if (filterStatus !== 'all') {
      uniqueBookings = uniqueBookings.filter(booking => {
        const status = getBookingStatus(booking);
        return status === filterStatus;
      });
    }

    // Apply text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      uniqueBookings = uniqueBookings.filter(booking => {
        const roomName = (booking.roomName || '').toLowerCase();
        const userName = (booking.userName || '').toLowerCase();
        const userEmail = (booking.userEmail || '').toLowerCase();
        const purpose = (booking.purpose || '').toLowerCase();
        const bookingId = (booking.bookingId || booking.id || '').toString().toLowerCase();

        return roomName.includes(query) ||
               userName.includes(query) ||
               userEmail.includes(query) ||
               purpose.includes(query) ||
               bookingId.includes(query);
      });
    }

    // Apply date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      uniqueBookings = uniqueBookings.filter(booking => {
        const bookingDate = new Date(booking.startTime);
        return bookingDate >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      uniqueBookings = uniqueBookings.filter(booking => {
        const bookingDate = new Date(booking.startTime);
        return bookingDate <= toDate;
      });
    }

    return uniqueBookings;
  }, [bookings, filterStatus, searchQuery, dateFrom, dateTo]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const uniqueBookings = bookings.filter(booking => {
      if (!booking.recurringGroupId) return true;
      return booking.isParentBooking === true;
    });

    const stats = {
      total: uniqueBookings.length,
      pending: 0,
      confirmed: 0,
      ongoing: 0,
      cancelled: 0,
      rejected: 0,
      completed: 0
    };

    uniqueBookings.forEach(booking => {
      const status = getBookingStatus(booking);
      if (stats.hasOwnProperty(status)) {
        stats[status]++;
      }
    });

    return stats;
  }, [bookings]);

  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  const getStatusBadgeClass = (booking) => {
    const status = getBookingStatus(booking);
    return `status-badge status-${status}`;
  };

  const getStatusLabel = (booking) => {
    const status = getBookingStatus(booking);
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Handle checkbox selection
  const handleSelectBooking = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  // Handle select all checkboxes
  const handleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.bookingId || b.id));
    }
  };

  // Open bulk delete modal
  const handleBulkDelete = () => {
    if (selectedBookings.length === 0) {
      toast.warning('Please select bookings to delete', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setShowBulkDeleteModal(true);
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    try {
      setIsProcessing(true);
      await conferenceRoomApi.bulkDeleteBookings(selectedBookings);
      toast.success(`${selectedBookings.length} booking(s) deleted successfully`, {
        position: "top-right",
        autoClose: 3000,
      });
      setSelectedBookings([]);
      setShowBulkDeleteModal(false);
      await fetchAllBookings();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to delete bookings';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if booking is eligible for feedback (completed meeting)
  const canAddFeedback = (booking) => {
    if (booking.feedbackRating) return false; // Already has feedback
    const status = getBookingStatus(booking);
    return status === 'completed';
  };

  // Open feedback modal
  const openFeedbackModal = (booking) => {
    setFeedbackBooking(booking);
    setFeedbackData({
      rating: booking.feedbackRating || 5,
      roomClean: booking.feedbackRoomClean ?? true,
      equipmentOk: booking.feedbackEquipmentOk ?? true,
      endedOnTime: booking.feedbackEndedOnTime ?? true,
      comments: booking.feedbackComments || ''
    });
    setShowFeedbackModal(true);
  };

  // Submit feedback
  const submitFeedback = async () => {
    if (!feedbackBooking) return;

    try {
      setIsSubmittingFeedback(true);
      await conferenceRoomApi.addBookingFeedback(feedbackBooking.bookingId || feedbackBooking.id, feedbackData);
      toast.success('Feedback submitted successfully', {
        position: "top-right",
        autoClose: 3000,
      });
      setShowFeedbackModal(false);
      setFeedbackBooking(null);
      await fetchAllBookings();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to submit feedback';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Render star rating
  const renderStars = (rating, interactive = false) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= rating ? 'filled' : ''} ${interactive ? 'clickable' : ''}`}
            onClick={interactive ? () => setFeedbackData(prev => ({ ...prev, rating: star })) : undefined}
          >
            {star <= rating ? <MdStar /> : <MdStarBorder />}
          </span>
        ))}
      </div>
    );
  };

  // Download filtered bookings as CSV
  const downloadCSV = () => {
    if (filteredBookings.length === 0) {
      toast.warning('No bookings to export', { position: "top-right", autoClose: 3000 });
      return;
    }

    const headers = [
      'Booking ID', 'Room Name', 'Tower', 'Floor', 'User Name', 'User Email',
      'Start Time', 'End Time', 'Duration', 'Purpose', 'Status', 'Type',
      'Feedback Rating', 'Room Clean', 'Equipment OK', 'Ended On Time', 'Feedback Comments'
    ];

    const rows = filteredBookings.map(booking => [
      booking.bookingId || booking.id || '',
      booking.roomName || '',
      booking.tower || '',
      booking.floor || '',
      booking.userName || booking.bookedBy || '',
      booking.userEmail || '',
      booking.startTime ? new Date(booking.startTime).toLocaleString() : '',
      booking.endTime ? new Date(booking.endTime).toLocaleString() : '',
      calculateDuration(booking.startTime, booking.endTime),
      booking.purpose || '',
      getBookingStatus(booking),
      booking.recurringGroupId ? 'Recurring' : 'Single',
      booking.feedbackRating || '',
      booking.feedbackRoomClean != null ? (booking.feedbackRoomClean ? 'Yes' : 'No') : '',
      booking.feedbackEquipmentOk != null ? (booking.feedbackEquipmentOk ? 'Yes' : 'No') : '',
      booking.feedbackEndedOnTime != null ? (booking.feedbackEndedOnTime ? 'Yes' : 'No') : '',
      booking.feedbackComments || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `all_bookings_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredBookings.length} bookings`, { position: "top-right", autoClose: 3000 });
  };

  return (
    <div className="admin-view all-bookings-view">
      <PageHeader
        breadcrumbs={["Community", "All Bookings"]}
      />

      {/* Compact Header with Stats and Refresh */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdInsertChart className="title-icon" />
            All Bookings
          </h2>
        </div>

        <div className="header-filters-compact">
          <button
            className="btn-book-compact"
            onClick={fetchAllBookings}
            disabled={isLoading}
          >
            <MdRefresh />
            <span>Refresh</span>
          </button>
          {selectedBookings.length > 0 && (
            <button
              className="btn-book-compact btn-delete-selected"
              onClick={handleBulkDelete}
            >
              <MdDelete />
              <span>Delete Selected ({selectedBookings.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="statistics-grid">
        <div className="stat-card stat-total">
          <div className="stat-icon">
            <MdInsertChart style={{ fontSize: '2rem', color: '#3b82f6' }} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{statistics.total}</h3>
            <p className="stat-label">Total Bookings</p>
          </div>
        </div>
        <div className="stat-card stat-confirmed">
          <div className="stat-icon">
            <MdCheckCircle style={{ fontSize: '2rem', color: '#10b981' }} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{statistics.confirmed}</h3>
            <p className="stat-label">Confirmed</p>
          </div>
        </div>
        <div className="stat-card stat-ongoing">
          <div className="stat-icon">
            <MdLoop style={{ fontSize: '2rem', color: '#8b5cf6' }} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{statistics.ongoing}</h3>
            <p className="stat-label">Ongoing</p>
          </div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-icon">
            <MdHourglassEmpty style={{ fontSize: '2rem', color: '#f59e0b' }} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{statistics.pending}</h3>
            <p className="stat-label">Pending</p>
          </div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-icon">
            <MdEventAvailable style={{ fontSize: '2rem', color: '#059669' }} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{statistics.completed}</h3>
            <p className="stat-label">Completed</p>
          </div>
        </div>
        <div className="stat-card stat-cancelled">
          <div className="stat-icon">
            <MdCancel style={{ fontSize: '2rem', color: '#ef4444' }} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{statistics.cancelled + statistics.rejected}</h3>
            <p className="stat-label">Cancelled/Rejected</p>
          </div>
        </div>
      </div>

      {/* Search, Date Filters and Download */}
      <div className="search-filter-row">
        <div className="search-box">
          <MdSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by room, user, email, purpose..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="date-filters">
          <div className="date-filter-group">
            <label>From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="date-filter-group">
            <label>To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
            />
          </div>
          {(dateFrom || dateTo || searchQuery) && (
            <button
              className="btn-clear-filters"
              onClick={() => {
                setSearchQuery('');
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear
            </button>
          )}
        </div>
        <button className="btn-download" onClick={downloadCSV}>
          <MdDownload />
          <span>Download CSV</span>
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="bookings-filters">
        <button
          className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          All Bookings <span className="filter-count">{statistics.total}</span>
        </button>
        <button
          className={`filter-tab ${filterStatus === 'pending' ? 'active' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          Pending <span className="filter-count">{statistics.pending}</span>
        </button>
        <button
          className={`filter-tab ${filterStatus === 'confirmed' ? 'active' : ''}`}
          onClick={() => setFilterStatus('confirmed')}
        >
          Confirmed <span className="filter-count">{statistics.confirmed}</span>
        </button>
        <button
          className={`filter-tab ${filterStatus === 'ongoing' ? 'active' : ''}`}
          onClick={() => setFilterStatus('ongoing')}
        >
          Ongoing <span className="filter-count">{statistics.ongoing}</span>
        </button>
        <button
          className={`filter-tab ${filterStatus === 'cancelled' ? 'active' : ''}`}
          onClick={() => setFilterStatus('cancelled')}
        >
          Cancelled <span className="filter-count">{statistics.cancelled}</span>
        </button>
        <button
          className={`filter-tab ${filterStatus === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          Rejected <span className="filter-count">{statistics.rejected}</span>
        </button>
        <button
          className={`filter-tab ${filterStatus === 'completed' ? 'active' : ''}`}
          onClick={() => setFilterStatus('completed')}
        >
          Completed <span className="filter-count">{statistics.completed}</span>
        </button>
      </div>

      {/* Bookings Table */}
      <div className="bookings-container">
        {isLoading ? (
          <div className="table card">
            <div className="table-head">
              <div className="cell">User</div>
              <div className="cell">Room</div>
              <div className="cell">Date & Time</div>
              <div className="cell">Duration</div>
              <div className="cell">Purpose</div>
              <div className="cell">Status</div>
            </div>
            {Array.from({ length: 5 }).map((_, idx) => (
              <SkeletonRow key={idx} />
            ))}
          </div>
        ) : filteredBookings.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="card bookings-table-desktop">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0}
                        onChange={handleSelectAll}
                        title="Select All"
                      />
                    </th>
                    <th style={{ width: '90px' }}>Booking ID</th>
                    <th style={{ width: '160px' }}>User</th>
                    <th style={{ width: '110px' }}>Room</th>
                    <th style={{ width: '170px' }}>Date & Time</th>
                    <th style={{ width: '80px' }}>Duration</th>
                    <th style={{ minWidth: '100px' }}>Purpose</th>
                    <th style={{ width: '110px' }}>Type</th>
                    <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                    <th style={{ width: '120px', textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking, idx) => (
                    <tr key={booking.bookingId || booking.id || idx}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedBookings.includes(booking.bookingId || booking.id)}
                          onChange={() => handleSelectBooking(booking.bookingId || booking.id)}
                        />
                      </td>
                      <td>
                        <span className="booking-id-cell" title={booking.bookingId || booking.id}>
                          {(booking.bookingId || booking.id || '').toString().slice(0, 8)}...
                        </span>
                      </td>
                      <td>
                        <div className="user-info">
                          <strong>{booking.userName || booking.bookedBy || 'Unknown'}</strong>
                          <small>{booking.userEmail || ''}</small>
                        </div>
                      </td>
                      <td>
                        <div className="room-info">
                          <strong>{booking.roomName || 'Conference Room'}</strong>
                          <small>{booking.tower || ''} {booking.floor || ''}</small>
                        </div>
                      </td>
                      <td>
                        <div className="datetime-info">
                          <div>{formatDateTime(booking.startTime)}</div>
                        </div>
                      </td>
                      <td>
                        <span className="duration-badge">
                          {calculateDuration(booking.startTime, booking.endTime)}
                        </span>
                      </td>
                      <td>
                        <span className="purpose-text">
                          {booking.purpose || '-'}
                        </span>
                      </td>
                      <td>
                        {booking.recurringGroupId ? (
                          <div className="recurring-info">
                            <span className="recurring-badge">
                              🔁 Recurring
                            </span>
                            <div className="recurring-details">
                              {booking.recurringBookingsCount && (
                                <span>{booking.recurringBookingsCount} bookings</span>
                              )}
                              {booking.recurringEndDate && (
                                <div>Till: {new Date(booking.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="single-badge">Single</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={getStatusBadgeClass(booking)}>
                          {getStatusLabel(booking)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {booking.feedbackRating ? (
                          <div className="feedback-display" title={booking.feedbackComments || 'No comments'}>
                            {renderStars(booking.feedbackRating)}
                          </div>
                        ) : canAddFeedback(booking) ? (
                          <button
                            className="btn-feedback"
                            onClick={() => openFeedbackModal(booking)}
                            title="Add feedback"
                          >
                            <MdFeedback /> Add
                          </button>
                        ) : (
                          <span className="no-feedback">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="bookings-cards-mobile">
              {filteredBookings.map((booking, idx) => (
                <div className="booking-card" key={booking.bookingId || booking.id || idx}>
                  <div className="booking-card-header">
                    <input
                      type="checkbox"
                      checked={selectedBookings.includes(booking.bookingId || booking.id)}
                      onChange={() => handleSelectBooking(booking.bookingId || booking.id)}
                      style={{ marginRight: '10px' }}
                    />
                    <div className="room-name">{booking.roomName || 'Conference Room'}</div>
                    <span className={getStatusBadgeClass(booking)}>
                      {getStatusLabel(booking)}
                    </span>
                  </div>
                  <div className="booking-card-body">
                    <div className="booking-detail">
                      <span className="label">Booking ID:</span>
                      <span className="value booking-id-cell" title={booking.bookingId || booking.id}>
                        {(booking.bookingId || booking.id || '').toString().slice(0, 8)}...
                      </span>
                    </div>
                    <div className="booking-detail">
                      <span className="label"><MdPerson style={{ marginRight: '4px', verticalAlign: 'middle' }} /> User:</span>
                      <span className="value">{booking.userName || booking.bookedBy || 'Unknown'}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="label"><MdEmail style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Email:</span>
                      <span className="value">{booking.userEmail || '-'}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="label"><MdCalendarToday style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Date & Time:</span>
                      <span className="value">{formatDateTime(booking.startTime)}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="label"><MdAccessTime style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Duration:</span>
                      <span className="value duration-badge">
                        {calculateDuration(booking.startTime, booking.endTime)}
                      </span>
                    </div>
                    <div className="booking-detail">
                      <span className="label"><MdLocationOn style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Location:</span>
                      <span className="value">{booking.tower || ''} {booking.floor || ''}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="label"><MdDescription style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Purpose:</span>
                      <span className="value">
                        {booking.purpose || '-'}
                      </span>
                    </div>
                    {booking.recurringGroupId ? (
                      <div className="booking-detail" style={{ backgroundColor: '#e3f2fd', padding: '8px', borderRadius: '6px', marginTop: '5px', border: '1px solid #90caf9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                          <MdLoop style={{ marginRight: '6px', color: '#1565c0' }} />
                          <strong style={{ color: '#1565c0' }}>Recurring Series</strong>
                        </div>
                        <div style={{ fontSize: '12px', color: '#333' }}>
                          {booking.recurringBookingsCount && (
                            <div>{booking.recurringBookingsCount} bookings in series</div>
                          )}
                          {booking.recurringEndDate && (
                            <div>Till: {new Date(booking.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="booking-detail">
                        <span className="label">Type:</span>
                        <span className="value">Single Booking</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty-state card">
            <div className="empty-icon"><MdEventBusy style={{ fontSize: '3rem', color: '#9ca3af' }} /></div>
            <h3>No {filterStatus !== 'all' ? filterStatus : ''} bookings found</h3>
            <p>
              {filterStatus === 'pending' && "There are no pending bookings."}
              {filterStatus === 'confirmed' && "There are no confirmed bookings."}
              {filterStatus === 'ongoing' && "There are no ongoing bookings."}
              {filterStatus === 'cancelled' && "There are no cancelled bookings."}
              {filterStatus === 'rejected' && "There are no rejected bookings."}
              {filterStatus === 'completed' && "There are no completed bookings."}
              {filterStatus === 'all' && "There are no bookings at the moment."}
            </p>
          </div>
        )}
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="modal-content action-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Multiple Bookings</h3>
              <button className="modal-close" onClick={() => setShowBulkDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete {selectedBookings.length} booking(s)?</p>
              <div className="expired-booking-warning" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
                <span className="warning-icon">⚠️</span>
                <div className="warning-content">
                  <strong>Warning!</strong>
                  <p>This action cannot be undone. All selected bookings will be permanently deleted.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={confirmBulkDelete}
                disabled={isProcessing}
              >
                {isProcessing ? 'Deleting...' : `Yes, Delete ${selectedBookings.length} Booking(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && feedbackBooking && (
        <div className="modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="modal-content feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><MdFeedback style={{ marginRight: '8px', verticalAlign: 'middle' }} />Add Meeting Feedback</h3>
              <button className="modal-close" onClick={() => setShowFeedbackModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="feedback-booking-info">
                <strong>{feedbackBooking.roomName}</strong>
                <span>{formatDateTime(feedbackBooking.startTime)}</span>
                <span>By: {feedbackBooking.userName || feedbackBooking.userEmail}</span>
              </div>

              <div className="feedback-form">
                <div className="feedback-field">
                  <label>Overall Etiquette Rating</label>
                  <div className="rating-input">
                    {renderStars(feedbackData.rating, true)}
                  </div>
                </div>

                <div className="feedback-checkboxes">
                  <label className="feedback-checkbox">
                    <input
                      type="checkbox"
                      checked={feedbackData.roomClean}
                      onChange={(e) => setFeedbackData(prev => ({ ...prev, roomClean: e.target.checked }))}
                    />
                    <span>Room left clean</span>
                  </label>
                  <label className="feedback-checkbox">
                    <input
                      type="checkbox"
                      checked={feedbackData.equipmentOk}
                      onChange={(e) => setFeedbackData(prev => ({ ...prev, equipmentOk: e.target.checked }))}
                    />
                    <span>Equipment properly handled</span>
                  </label>
                  <label className="feedback-checkbox">
                    <input
                      type="checkbox"
                      checked={feedbackData.endedOnTime}
                      onChange={(e) => setFeedbackData(prev => ({ ...prev, endedOnTime: e.target.checked }))}
                    />
                    <span>Meeting ended on time</span>
                  </label>
                </div>

                <div className="feedback-field">
                  <label>Comments (Optional)</label>
                  <textarea
                    value={feedbackData.comments}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, comments: e.target.value }))}
                    placeholder="Any issues or observations..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowFeedbackModal(false)}
                disabled={isSubmittingFeedback}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={submitFeedback}
                disabled={isSubmittingFeedback}
              >
                {isSubmittingFeedback ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllBookings;
